import { memo, useCallback, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '@/components/_ui/modal';
import { Star } from 'lucide-react';
import PaginationLimit from '@/components/ui/pagination-limit';
import Pagination from '@/components/ui/pagination';
import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { CsATDetailAnalytics } from '@/features/customers/customer-survey/types/customer-survey.type';

export type TCSATResponseDetailModalType = 'CSATResponseDetailModal';

export interface ICSATResponseDetailModalProps {
  questionText?: string;
  responses?: CsATDetailAnalytics[];
}

// Mock data type - TODO: Replace with actual API types
type InternalResponse = {
  id: string;
  customerName?: string;
  rating: number;
  createdAt?: string;
};

const CSATResponseDetailModal = memo(({ questionText, responses }: ICSATResponseDetailModalProps) => {
  // Local pagination (client-side) — data is provided from parent
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const customerModal = useCustomerModuleModalRootActionsStore();

  // Use responses passed from caller
  const isLoading = false;

  const allData: InternalResponse[] = (responses || []).map((r: CsATDetailAnalytics, idx: number) => ({
    id: `${r.customerName ?? 'anon'}_${idx}`,
    customerName: r.customerName,
    rating: Number(r.value) || 0,
    createdAt: r.createdAt,
  }));

  const totalItems = allData.length;
  const totalPages = Math.ceil(totalItems / perPage) || 1;
  const currentData = allData.slice((page - 1) * perPage, page * perPage);

  const handleClose = useCallback(() => {
    customerModal.closeModal('CSATResponseDetailModal');
  }, []);

  const renderStars = useCallback((rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-[16px] h-[16px] ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  }, []);

  const isOpen = useMemo(() => {
    return customerModal.checkIsOpenModal('CSATResponseDetailModal');
  }, []);

  return (
    <Modal isOpen={isOpen} lockBodyScroll onClose={handleClose} className="md:w-[700px] w-[345px] md:p-[32px] p-[16px]">
      <ModalHeader>
        <ModalTitle>{questionText ?? 'Responses Detail'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="w-full">
          {isLoading ? (
            <CSATResponseTableSkeleton />
          ) : (
            <>
              <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
                <table className="w-full text-sm text-left rtl:text-right">
                  <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
                    <tr>
                      <th className="w-[60px] text-center py-3">No</th>
                      <th className="w-[300px] px-6 py-3">Customer Name</th>
                      <th className="px-6 py-3">Feedback Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((response, index: number) => (
                      <tr
                        key={response.id}
                        className={`bg-white ${index !== currentData.length - 1 ? 'border-b border-[#D9D9D9]' : ''}`}
                      >
                        <th className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words">
                          {(page - 1) * perPage + index + 1}
                        </th>
                        <td className="w-[300px] px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words">
                          {response.customerName || '-'}
                        </td>
                        <td className="px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words flex gap-[12px]">
                          {renderStars(response.rating)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination (client-side) */}
              <div className="flex justify-between items-center py-3 px-4 space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
                <div className="text-[14px] font-normal text-[#25151E]">Total {totalItems} entries</div>
                <div className="flex space-x-2">
                  <PaginationLimit
                    value={perPage}
                    onChange={(v: number) => {
                      setPerPage(v);
                      setPage(1);
                    }}
                  />
                  <Pagination limit={perPage} page={page} count={totalPages} onPageChange={(p: number) => setPage(p)} />
                </div>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
});

CSATResponseDetailModal.displayName = 'CSATResponseDetailModal';
export default CSATResponseDetailModal;

// ------------------------------------------------------------------------
// CSAT Response Table Skeleton

const CSATResponseTableSkeleton = memo(() => {
  return (
    <div className="mt-[20px]">
      <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
        <table className="w-full text-sm text-left rtl:text-right">
          <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
            <tr>
              <th className="w-[60px] text-center py-3">No</th>
              <th className="w-[300px] px-6 py-3">Customer Name</th>
              <th className="px-6 py-3">Feedback Rating</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr key={index} className={`bg-white ${index !== 4 ? 'border-b border-[#D9D9D9]' : ''}`}>
                <th className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words">
                  <div className="h-4 w-8 bg-gray-200 rounded animate-pulse mx-auto" />
                </th>
                <td className="w-[300px] px-6 py-3">
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-[12px]">
                    {[...Array(5)].map((_, starIndex) => (
                      <div key={starIndex} className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-between items-center py-3 px-4 space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="flex space-x-2">
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
});
CSATResponseTableSkeleton.displayName = 'CSATResponseTableSkeleton';
